import classNames from 'classnames';
import * as _ from 'lodash-es';
import Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Slot from '../components/Slot';
import { useQuill } from '../editor/QuillContext';
import { useUIElementResizeListener } from '../editor/UIElementResizeContext';
import { getHeaderCollapse } from '../editor/useHeaderCollapse';
import { getListCollapse } from '../editor/useListCollapse';
import addTestId from '../utils/addTestId';
import * as styles from './MagneticWallLayout.module.scss';
import type {
  Layout,
  MagneticWallState,
} from './comment/getMagneticWallItemLayout';
import getMagneticWallItemLayout, {
  HeightState,
  VerticalSpan,
} from './comment/getMagneticWallItemLayout';
import {
  getScrollContainer,
  useListenToPostScroll,
} from './listenToPostScroll';
import { MainEditorResizeHandler } from './useOnMainEditorResize';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import type { RelativeDOMRect } from './utils/toRelativeDOMRect';
import toRelativeDOMRect from './utils/toRelativeDOMRect';

const DATA_MAGNETIC_WALL_ITEM_ID = 'data-magnetic-wall-item-id';

export function queryMagneticWallItem(id: string) {
  return document.querySelector(`[${DATA_MAGNETIC_WALL_ITEM_ID}="${id}"]`);
}

enum DEBOUNCE_DELAY {
  Fast = 100,
  Slow = 1000,
}

interface useUpdateThreadHeightsArgs {
  stateRef: React.MutableRefObject<MagneticWallState>;
  /**
   * @param avoidRepositioningFocusedThread - If true, the focused thread will not be repositioned.
   * This is needed because in some cases we don't want to reposition the focused thread to avoid interrupting the user. E.g. when user is typing in the comment input.
   */
  updatePositions: (avoidRepositioningFocusedThread?: boolean) => void;
}

export const MagneticWallLayoutContext = React.createContext<{
  debouncedUpdateDesiredPositions: () => void;
  updateThreadHeights: (avoidRepositioningFocusedThread?: boolean) => void;
  handlePopOverRender: (itemId: string, height: number) => void;
  layout: Layout;
} | null>(null);

export const useMagneticWallLayout = () => {
  const context = React.useContext(MagneticWallLayoutContext);
  if (!context) {
    throw new Error(
      'useMagneticWallLayout must be used within a MagneticWallLayoutContext',
    );
  }
  return context;
};

const useUpdateThreadHeights = ({
  stateRef,
  updatePositions,
}: useUpdateThreadHeightsArgs) => {
  const avoidRepositioningFocusedThreadRef = useRef<boolean | null>(null);

  const debouncedHandleThreadResize = useDebouncedCallback(() => {
    const shouldAvoid = avoidRepositioningFocusedThreadRef.current ?? false;
    avoidRepositioningFocusedThreadRef.current = null;

    const { magneticWallItems, focus, heights } = stateRef.current;
    if (!magneticWallItems.length) return;

    magneticWallItems.forEach((item) => {
      const node = queryMagneticWallItem(item.id);

      const existing = heights[item.id] ?? {
        [HeightState.Focus]: null,
        [HeightState.Normal]: null,
      };

      const height = node instanceof HTMLElement ? node.offsetHeight : null;
      if (focus?.id === item.id) {
        existing[HeightState.Focus] = height;
      } else {
        existing[HeightState.Normal] = height;

        // Set the focus height if it hasn't been set yet to improve animations.
        // Future: This could be even improved by estimating the height based on the content.
        if (existing[HeightState.Focus] == null) {
          existing[HeightState.Focus] = height;
        }
      }
      heights[item.id] = existing;
    });

    updatePositions(shouldAvoid);
  }, DEBOUNCE_DELAY.Fast);

  return useCallback(
    (avoidRepositioningFocusedThread = false) => {
      // Falsy values will take priority over truthy values.
      if (!avoidRepositioningFocusedThread) {
        avoidRepositioningFocusedThreadRef.current = false;
      } else if (avoidRepositioningFocusedThreadRef.current == null) {
        avoidRepositioningFocusedThreadRef.current = true;
      }

      debouncedHandleThreadResize();
    },
    [debouncedHandleThreadResize],
  );
};

const useLayout = (
  stateRef: React.MutableRefObject<MagneticWallState>,
  {
    spacing,
    minItemHeight,
  }: {
    spacing?: number;
    minItemHeight?: number;
  },
) => {
  const [layout, setLayout] = useState<
    Layout & { focusedThreadId: string | null }
  >(() => ({
    ...getMagneticWallItemLayout(stateRef.current, {
      minItemHeight,
      spacing,
    }),
    focusedThreadId: null,
  }));

  const updateLayout = useCallback(
    (avoidRepositioningFocusedThread = false) => {
      setLayout((prev) => {
        const state = stateRef.current;
        const focusedThreadId = state.focus?.id ?? null;

        const previousTop = focusedThreadId && prev.tops[focusedThreadId];

        const shouldReserve =
          avoidRepositioningFocusedThread &&
          focusedThreadId &&
          typeof previousTop === 'number' &&
          focusedThreadId === prev.focusedThreadId;

        const next = {
          ...getMagneticWallItemLayout(state, {
            reserved: shouldReserve
              ? {
                  id: focusedThreadId,
                  top: previousTop,
                  compactPopOverSide: prev.compactPopOverSide,
                }
              : undefined,
            spacing,
            minItemHeight,
          }),
          focusedThreadId,
        };
        return _.isEqual(prev, next) ? prev : next;
      });
    },
    [stateRef, spacing, minItemHeight],
  );

  return { layout, updateLayout };
};

export type RelativeContentRects = {
  rect: RelativeDOMRect;
  id: string;
};

export function reduceContentRectsToMap(
  contentRects: RelativeContentRects[],
): Record<string, DOMRect> {
  return contentRects.reduce(
    (memo, { id, rect }) => {
      const existing = memo[id];
      if (existing != null) {
        const top = Math.min(rect.top, existing.top);
        return {
          ...memo,
          [id]: DOMRect.fromRect({
            x: existing.x,
            y: top,
            height: Math.max(rect.bottom, existing.bottom) - top,
            width: existing.width,
          }),
        };
      }
      return {
        ...memo,
        [id]: rect,
      };
    },
    {} as Record<string, DOMRect>,
  );
}

interface MagneticWallLayoutRootProps<
  Item extends MagneticWallState['magneticWallItems'][number],
> {
  children: React.ReactNode;
  focus: MagneticWallState['focus'];
  magneticWallItems: Item[];
  getRelativeContentRects: () => RelativeContentRects[];
  getRestrictedSpans?: (
    magneticWallRect: DOMRect,
  ) => MagneticWallState['restrictedSpans'];
  hasBanner?: boolean;
  itemSize: MagneticWallState['itemSize'];
  containerRef?: React.Ref<HTMLDivElement>;
  spacing?: number;
  minItemHeight?: number;
}

const MagneticWallLayoutRoot = <
  T extends MagneticWallState['magneticWallItems'][number],
>({
  children,
  focus,
  magneticWallItems,
  getRelativeContentRects,
  getRestrictedSpans,
  hasBanner,
  itemSize,
  containerRef,
  spacing,
  minItemHeight,
}: MagneticWallLayoutRootProps<T>) => {
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const { quill } = useQuill();

  const stateRef = useRef<MagneticWallState>({
    contentRects: {},
    heights: {},
    compactPopOverHeights: {},
    restrictedSpans: [],
    magneticWallItems,
    itemSize,
    focus,
    scrollingContainerSpan: new VerticalSpan(-Infinity, Infinity),
  });

  const { layout, updateLayout } = useLayout(stateRef, {
    spacing,
    minItemHeight,
  });

  const optimisticItemContentRects = useOptimisticItemContentRects();

  useEffect(() => {
    if (!optimisticItemContentRects.size) return;

    optimisticItemContentRects.entries().forEach(([id, rect]) => {
      stateRef.current.contentRects[id] = rect;
    });
  }, [quill, magneticWallItems, optimisticItemContentRects]);

  const updateScrollingContainerVerticalBounds = useCallback(() => {
    const scrollingContainer = getScrollContainer();
    if (scrollingContainer && quill) {
      stateRef.current.scrollingContainerSpan = VerticalSpan.fromDOMRect(
        toRelativeDOMRect(
          scrollingContainer.getBoundingClientRect(),
          quill.container.getBoundingClientRect(),
        ),
      );
    }
  }, [quill]);

  useLayoutEffect(() => {
    stateRef.current.magneticWallItems = magneticWallItems;
    stateRef.current.itemSize = itemSize;

    updateLayout();
  }, [magneticWallItems, itemSize, updateLayout]);

  useLayoutEffect(() => {
    stateRef.current.focus = focus;

    if (focus) {
      updateScrollingContainerVerticalBounds();
    }

    updateLayout();
  }, [focus, updateLayout, updateScrollingContainerVerticalBounds]);

  const updateDesiredPositions = () => {
    stateRef.current.contentRects = reduceContentRectsToMap(
      getRelativeContentRects(),
    );

    const containerEl = innerContainerRef.current;

    if (containerEl && getRestrictedSpans) {
      stateRef.current.restrictedSpans = getRestrictedSpans(
        containerEl.getBoundingClientRect(),
      );
    }

    updateLayout();
  };

  const updateThreadHeights = useUpdateThreadHeights({
    stateRef,
    updatePositions: updateLayout,
  });

  useEffect(() => {
    updateThreadHeights();
  }, [magneticWallItems, focus, itemSize, updateThreadHeights]);

  const debouncedUpdateScrollingContainerVerticalBounds = useDebouncedCallback(
    updateScrollingContainerVerticalBounds,
    DEBOUNCE_DELAY.Fast,
  );

  const debouncedUpdateDesiredPositions = useDebouncedCallback(
    updateDesiredPositions,
    DEBOUNCE_DELAY.Fast,
  );

  const debouncedHandlePostScroll = useDebouncedCallback(
    (avoidRepositioningFocusedThread?: boolean) => {
      updateScrollingContainerVerticalBounds();
      updateLayout(avoidRepositioningFocusedThread);
    },
    DEBOUNCE_DELAY.Slow,
  );

  useListenToPostScroll(({ isScrolledWithUserInteraction }) => {
    if (isScrolledWithUserInteraction) {
      debouncedHandlePostScroll(true);
    } else {
      // When scrolling happens programmatically (e.g. scrolling to an anchor on page load),
      // allow the focused thread to reposition so it remains visible in the viewport
      debouncedHandlePostScroll(false);
      debouncedHandlePostScroll.flush();
    }
  });

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    debouncedUpdateScrollingContainerVerticalBounds();
  }, [
    hasBanner,
    debouncedUpdateDesiredPositions,
    debouncedUpdateScrollingContainerVerticalBounds,
  ]);

  useLayoutEffect(() => {
    if (!innerContainerRef.current) return undefined;
    let lastWidth: number | null = null;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width } = entry.contentRect;
      if (width !== lastWidth && lastWidth !== null) {
        debouncedUpdateDesiredPositions();
      }
      lastWidth = width;
    });
    resizeObserver.observe(innerContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [debouncedUpdateDesiredPositions]);

  const addUIElementResizeListener = useUIElementResizeListener();

  useEffect(() => {
    if (!quill) return undefined;
    quill.on(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

    const removeUIElementResizeListener: () => void =
      addUIElementResizeListener(debouncedUpdateDesiredPositions);
    const unsubscribeCollapseStateListener =
      getHeaderCollapse(quill)?.subscribeToLocalCollapseStateChange(
        debouncedUpdateDesiredPositions,
      ) ?? (() => {});

    const unsubscribeListItemCollapsedStateListener = getListCollapse(
      quill,
    )?.subscribeToLocalCollapseStateChange(debouncedUpdateDesiredPositions);

    debouncedUpdateDesiredPositions();

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

      removeUIElementResizeListener?.();
      unsubscribeListItemCollapsedStateListener?.();
      unsubscribeCollapseStateListener();
    };
  }, [quill, debouncedUpdateDesiredPositions, addUIElementResizeListener]);

  const handlePopOverRender = useCallback(
    (itemId: string, height: number) => {
      stateRef.current.compactPopOverHeights[itemId] = height;
      updateLayout();
    },
    [updateLayout],
  );

  const contextValue = useMemo(
    () => ({
      debouncedUpdateDesiredPositions,
      updateThreadHeights,
      handlePopOverRender,
      layout,
    }),
    [
      debouncedUpdateDesiredPositions,
      updateThreadHeights,
      handlePopOverRender,
      layout,
    ],
  );

  return (
    <MainEditorResizeHandler
      onMainEditorResize={() => {
        debouncedUpdateScrollingContainerVerticalBounds();
        updateThreadHeights();
        debouncedUpdateDesiredPositions();
      }}
    >
      <Slot ref={containerRef}>
        <div
          ref={innerContainerRef}
          className={styles.container}
          {...addTestId('magneticWall')}
          style={{
            minHeight: layout.magneticWallMinHeight ?? undefined,
          }}
        >
          <MagneticWallLayoutContext.Provider value={contextValue}>
            {children}
          </MagneticWallLayoutContext.Provider>
        </div>
      </Slot>
    </MainEditorResizeHandler>
  );
};

interface MagneticWallLayoutItem {
  children: React.ReactNode;
  focused: boolean;
  top: number;
  id: string;
  className?: string;
}

const MagneticWallLayoutItem = React.forwardRef<
  HTMLDivElement,
  MagneticWallLayoutItem
>(({ children, top, focused, id, className }, ref) => {
  return (
    <div
      {...{ [DATA_MAGNETIC_WALL_ITEM_ID]: id }}
      className={classNames(styles.item, className)}
      data-is-focused={focused}
      style={{ top }}
      ref={ref}
    >
      {children}
    </div>
  );
});

export default { Root: MagneticWallLayoutRoot, Item: MagneticWallLayoutItem };
